import type { Cell } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import type { Virtualizer } from "@tanstack/react-virtual";
import type { Table as TableType } from "@tanstack/table-core/build/lib/types";
import clsx from "clsx";

import TableBodyCell from "~/components/Table/components/TableBodyCell";

type VirtualizedTableBodyContentProps<T> = {
	disableUnselectableRows?: boolean;
	fontSize?: "sm" | "xs";
	table: TableType<T>;
	verticalBorders?: boolean;
	virtualizer: Virtualizer<HTMLDivElement, Element>;
	horizontalBorders?: boolean;
};

const VirtualizedTableBodyContent = <T,>({
	disableUnselectableRows = false,
	fontSize = "sm",
	table,
	verticalBorders,
	virtualizer,
	horizontalBorders = true,
}: VirtualizedTableBodyContentProps<T>) => {
	const { rows } = table.getRowModel();
	const virtualRows = virtualizer.getVirtualItems();

	// Add padding rows to maintain scroll height
	const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
	const paddingBottom = virtualRows.length > 0
		? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
		: 0;

	return (
		<>
			{paddingTop > 0 && (
				<tr>
					<td style={{ height: paddingTop }} />
				</tr>
			)}
			{virtualRows.map((virtualRow) => {
				const row = rows[virtualRow.index];
				const onRowClick = table.options.meta ? table.options.meta.onRowClick : undefined;
				const rowIsSelected = row.getIsSelected();
				const rowCannotBeSelected = disableUnselectableRows ? !row.getCanSelect() : null;

				return (
					<tr
						className={clsx(
							"group",
							rowCannotBeSelected === true && "pointer-events-none opacity-30",
							onRowClick && "cursor-pointer",
							verticalBorders && "divide-x divide-gray-200 hover:divide-white",
							rowIsSelected && "bg-primary-200",
							horizontalBorders && "border-t border-gray-200 first:border-t-0",
						)}
						key={row.id}
						onClick={onRowClick ? (event) => onRowClick(row, event) : undefined}
						style={{
							height: `${virtualRow.size}px`,
						}}
					>
						{row.getVisibleCells().map((cell: Cell<T, any>) => {
							return (
								<TableBodyCell
									key={cell.id}
									fontSize={fontSize}
									textAlign={cell.column.columnDef.meta?.cellTextAlign}
									hasWidthRestriction={cell.column.columnDef.meta?.width !== undefined}
									hasPadding={cell.column.columnDef.meta?.hasPadding}
									className={clsx(
										cell.column.getIsPinned() && "sticky left-0 z-10 bg-white",
										rowIsSelected
											? "group-hover:bg-primary-300"
											: "group-hover:bg-gray-200",
									)}
								>
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</TableBodyCell>
							);
						})}
					</tr>
				);
			})}
			{paddingBottom > 0 && (
				<tr>
					<td style={{ height: paddingBottom }} />
				</tr>
			)}
		</>
	);
};

export default VirtualizedTableBodyContent;